import { cs } from '../../lib/currency';
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Edit2,
  Trash2,
  Users,
  Calendar,
  Megaphone,
  MapPin,
  DollarSign,
  Clock,
  Eye,
  Tent,
  UserCheck,
  XCircle,
  RefreshCw,
  Percent,
  Copy,
} from 'lucide-react';
import api from '../../services/api';
import { useFeature } from '../../hooks/useFeature';
import RichTextEditor from '../../components/RichTextEditor';
import RefundDialog from '../../components/admin/RefundDialog';
import NetAmount from '../../components/shared/NetAmount';
import { formatDateOnly } from '../../utils/timezone';
import { addDaysToDateString } from '../../utils/businessTime';

const dateOpts = { weekday: undefined, year: 'numeric', month: 'numeric', day: 'numeric' };

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
];

const EXPERIENCE_LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'competitive', label: 'Competitive' },
];

const CampManagement = () => {
  // iter317 — Phase 7B plan gate. Gates CREATE only; existing camps
  // remain editable so admins can finish in-flight maintenance even
  // after a plan downgrade.
  const campsFeature = useFeature('camps.core');
  const [loading, setLoading] = useState(true);
  const [camps, setCamps] = useState([]);
  const [activeTab, setActiveTab] = useState('camps');
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [registrations, setRegistrations] = useState([]);

  // Refund dialog state (uses reusable RefundDialog)
  const [refundOpen, setRefundOpen] = useState(false);
  const [refundReg, setRefundReg] = useState(null);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);

  // iter306h: discount + pricing dialog state for admin overrides.
  const [discountOpen, setDiscountOpen] = useState(false);
  const [discountReg, setDiscountReg] = useState(null);
  const [discountForm, setDiscountForm] = useState({ discount_amount: '', discount_reason: '' });
  const [discountSaving, setDiscountSaving] = useState(false);

  const [pricingOpen, setPricingOpen] = useState(false);
  const [pricingReg, setPricingReg] = useState(null);
  const [pricingForm, setPricingForm] = useState({ selected_weeks: [], payment_amount: '', deposit_amount: '' });
  const [pricingSaving, setPricingSaving] = useState(false);
  // iter306k: refund confirmation when pricing reduction triggers
  // over-paid scenario. Backend returns `refund.required=true` + the
  // `refund.overpaid` amount; we open this modal to collect method.
  const [refundConfirm, setRefundConfirm] = useState(null); // { reg, overpaid, payload }
  const [refundMethod, setRefundMethod] = useState('stripe');
  const [refundReason, setRefundReason] = useState('');
  const [refundConfirming, setRefundConfirming] = useState(false);
  
  // Dialog states
  const [campDialogOpen, setCampDialogOpen] = useState(false);
  const [editingCamp, setEditingCamp] = useState(null);
  const [announcingCampId, setAnnouncingCampId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    camp_type: 'single',
    start_date: '',
    end_date: '',
    days_of_week: [],
    start_time: '09:00',
    end_time: '12:00',
    location: '',
    fee: '',
    price_per_week: '',
    deposit_amount: '',
    multi_week_discount_type: '',
    multi_week_discount_value: '',
    min_weeks_for_discount: '',
    weeks: [],
    max_participants: '',
    min_age: '',
    max_age: '',
    experience_levels: [],
    is_active: true,
    registration_deadline: '',
    capture_experience: true,
    capture_medical_notes: false,
    additional_info_label: '',
    terms_and_conditions: ''
  });

  useEffect(() => {
    fetchCamps();
  }, []);

  const fetchCamps = async () => {
    try {
      const response = await api.get('/camps');
      setCamps(response.data || []);
    } catch (error) {
      toast.error('Failed to load camps');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (campId) => {
    setLoadingRegistrations(true);
    try {
      const response = await api.get(`/camps/${campId}/registrations`);
      setRegistrations(response.data || []);
    } catch (error) {
      toast.error('Failed to load registrations');
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const openCampDialog = (camp = null) => {
    // iter317 — Phase 7B plan gate. Only the CREATE path is gated; the
    // EDIT path (camp != null) is always allowed so admins on a
    // downgraded plan can still finish maintenance on existing camps.
    if (!camp && !campsFeature.enabled) {
      campsFeature.prompt('Creating a camp');
      return;
    }
    if (camp) {
      setEditingCamp(camp);
      setFormData({
        name: camp.name,
        description: camp.description,
        camp_type: camp.camp_type,
        start_date: camp.start_date,
        end_date: camp.end_date,
        days_of_week: camp.days_of_week || [],
        start_time: camp.start_time,
        end_time: camp.end_time,
        location: camp.location || '',
        fee: camp.fee?.toString() || '',
        price_per_week: camp.price_per_week?.toString() || '',
        deposit_amount: camp.deposit_amount?.toString() || '',
        multi_week_discount_type: camp.multi_week_discount_type || '',
        multi_week_discount_value: camp.multi_week_discount_value?.toString() || '',
        min_weeks_for_discount: camp.min_weeks_for_discount?.toString() || '',
        weeks: camp.weeks || [],
        max_participants: camp.max_participants?.toString() || '',
        min_age: camp.min_age?.toString() || '',
        max_age: camp.max_age?.toString() || '',
        experience_levels: camp.experience_levels || [],
        is_active: camp.is_active,
        registration_deadline: camp.registration_deadline || '',
        capture_experience: camp.capture_experience ?? true,
        capture_medical_notes: camp.capture_medical_notes ?? false,
        additional_info_label: camp.additional_info_label || '',
        terms_and_conditions: camp.terms_and_conditions || ''
      });
    } else {
      setEditingCamp(null);
      setFormData({
        name: '',
        description: '',
        camp_type: 'single',
        start_date: '',
        end_date: '',
        days_of_week: [],
        start_time: '09:00',
        end_time: '12:00',
        location: '',
        fee: '',
        price_per_week: '',
        deposit_amount: '',
        multi_week_discount_type: '',
        multi_week_discount_value: '',
        min_weeks_for_discount: '',
        weeks: [],
        max_participants: '',
        min_age: '',
        max_age: '',
        experience_levels: [],
        is_active: true,
        registration_deadline: '',
        capture_experience: true,
        capture_medical_notes: false,
        additional_info_label: '',
        terms_and_conditions: ''
      });
    }
    setCampDialogOpen(true);
  };

  const handleDayToggle = (day) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  const handleExperienceToggle = (level) => {
    setFormData(prev => ({
      ...prev,
      experience_levels: prev.experience_levels.includes(level)
        ? prev.experience_levels.filter(l => l !== level)
        : [...prev.experience_levels, level]
    }));
  };

  // Generate weeks based on start date and number of weeks
  // iter306hh: pure YYYY-MM-DD string math so DST transitions inside
  // the camp window can never shift a week boundary by a day.
  const generateWeeks = (startDate, numWeeks) => {
    if (!startDate || !numWeeks || numWeeks < 1) return [];

    const weeks = [];
    for (let i = 0; i < numWeeks; i++) {
      const weekStart = addDaysToDateString(startDate, i * 7);
      const weekEnd = addDaysToDateString(weekStart, 6);
      weeks.push({
        week_number: i + 1,
        start_date: weekStart,
        end_date: weekEnd,
        is_available: true
      });
    }

    return weeks;
  };

  const handleGenerateWeeks = () => {
    if (!formData.start_date) {
      toast.error('Please set a start date first');
      return;
    }
    
    const numWeeks = parseInt(prompt('How many weeks?', '4'));
    if (!numWeeks || numWeeks < 1) return;
    
    const weeks = generateWeeks(formData.start_date, numWeeks);
    
    // Calculate end date based on last week
    const lastWeek = weeks[weeks.length - 1];
    
    setFormData(prev => ({
      ...prev,
      weeks,
      end_date: lastWeek.end_date
    }));
    
    toast.success(`Generated ${numWeeks} weeks`);
  };

  const toggleWeekAvailability = (weekNumber) => {
    setFormData(prev => ({
      ...prev,
      weeks: prev.weeks.map(w => 
        w.week_number === weekNumber 
          ? { ...w, is_available: !w.is_available }
          : w
      )
    }));
  };

  const removeWeek = (weekNumber) => {
    setFormData(prev => ({
      ...prev,
      weeks: prev.weeks.filter(w => w.week_number !== weekNumber)
        .map((w, idx) => ({ ...w, week_number: idx + 1 }))
    }));
  };

  const saveCamp = async () => {
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For multi-week camps, require price_per_week; for single, require fee
    if (formData.camp_type === 'recurring' && formData.weeks.length > 0) {
      if (!formData.price_per_week) {
        toast.error('Please set a price per week for multi-week camps');
        return;
      }
    } else {
      if (!formData.fee) {
        toast.error('Please set a fee for the camp');
        return;
      }
    }
    
    if (formData.days_of_week.length === 0) {
      toast.error('Please select at least one day of the week');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        fee: parseFloat(formData.fee) || 0,
        price_per_week: formData.price_per_week ? parseFloat(formData.price_per_week) : null,
        deposit_amount: formData.deposit_amount ? parseFloat(formData.deposit_amount) : null,
        multi_week_discount_type: formData.multi_week_discount_type || null,
        multi_week_discount_value: formData.multi_week_discount_value ? parseFloat(formData.multi_week_discount_value) : null,
        min_weeks_for_discount: formData.min_weeks_for_discount ? parseInt(formData.min_weeks_for_discount) : null,
        weeks: formData.weeks.length > 0 ? formData.weeks : null,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null,
        min_age: formData.min_age ? parseInt(formData.min_age) : null,
        max_age: formData.max_age ? parseInt(formData.max_age) : null,
      };

      if (editingCamp) {
        await api.put(`/camps/${editingCamp.id}`, payload);
        toast.success('Camp updated successfully');
      } else {
        await api.post('/camps', payload);
        toast.success('Camp created successfully');
      }
      
      setCampDialogOpen(false);
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save camp');
    } finally {
      setSaving(false);
    }
  };

  const deleteCamp = async (campId) => {
    if (!window.confirm('Are you sure you want to delete this camp?')) return;
    
    try {
      await api.delete(`/camps/${campId}`);
      toast.success('Camp deleted');
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete camp');
    }
  };

  const toggleCamp = async (campId) => {
    try {
      const response = await api.patch(`/camps/${campId}/toggle`);
      toast.success(response.data.message);
      fetchCamps();
    } catch (error) {
      toast.error('Failed to toggle camp status');
    }
  };

  const duplicateCamp = async (camp) => {
    if (!camp?.id) return;
    const ok = window.confirm(
      `Duplicate "${camp.name}"?\n\n`
      + 'A draft copy will be created (inactive) with all settings, '
      + 'description, weeks, pricing and terms cloned. You can then edit '
      + 'the dates and publish it.'
    );
    if (!ok) return;
    try {
      const { data } = await api.post(`/camps/${camp.id}/duplicate`);
      toast.success(`Created draft: ${data?.name || 'Copy'}`);
      fetchCamps();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to duplicate camp');
    }
  };

  const announceCamp = async (camp) => {
    if (!camp?.id) return;
    const ok = window.confirm(
      `Send an in-app notification to ALL parents and customers about "${camp.name}"?\n\n`
      + 'This cannot be undone, but recipients who were already notified about this camp will be skipped.'
    );
    if (!ok) return;
    setAnnouncingCampId(camp.id);
    try {
      const { data } = await api.post(`/camps/${camp.id}/announce`);
      const recips = data?.recipients ?? 0;
      const skipped = data?.skipped ?? 0;
      if (recips === 0) {
        toast.info(data?.reason || 'Everyone has already been notified about this camp.');
      } else {
        toast.success(`Announcement sent to ${recips} recipient${recips === 1 ? '' : 's'}${skipped > 0 ? ` (${skipped} already notified)` : ''}.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send announcement');
    } finally {
      setAnnouncingCampId(null);
    }
  };

  const viewRegistrations = (camp) => {
    setSelectedCamp(camp);
    fetchRegistrations(camp.id);
    setActiveTab('registrations');
  };

  const updateRegistrationStatus = async (registrationId, status) => {
    try {
      await api.patch(`/camps/registrations/${registrationId}/status?status=${status}`);
      toast.success(`Registration ${status}`);
      if (selectedCamp) {
        fetchRegistrations(selectedCamp.id);
      }
    } catch (error) {
      toast.error('Failed to update registration');
    }
  };

  const updatePaymentStatus = async (registrationId, paymentStatus) => {
    try {
      await api.patch(`/camps/registrations/${registrationId}/payment?payment_status=${paymentStatus}`);
      toast.success(`Payment marked as ${paymentStatus}`);
      if (selectedCamp) {
        fetchRegistrations(selectedCamp.id);
      }
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  // iter306h: admin pricing controls — open dialogs prefilled with the
  // registration's current values; submit calls the new camps router
  // endpoints which recompute the deposit + balance manual_invoices.
  const openDiscountDialog = (reg) => {
    setDiscountReg(reg);
    setDiscountForm({
      discount_amount: reg.discount_amount ? String(reg.discount_amount) : '',
      discount_reason: reg.discount_reason || '',
    });
    setDiscountOpen(true);
  };

  const submitDiscount = async () => {
    if (!discountReg) return;
    const amt = Number(discountForm.discount_amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error('Enter a valid discount amount');
      return;
    }
    try {
      setDiscountSaving(true);
      await api.patch(`/camps/registrations/${discountReg.id}/discount`, {
        discount_amount: amt,
        discount_reason: discountForm.discount_reason || null,
      });
      toast.success('Discount applied');
      setDiscountOpen(false);
      if (selectedCamp) fetchRegistrations(selectedCamp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply discount');
    } finally {
      setDiscountSaving(false);
    }
  };

  const openPricingDialog = (reg) => {
    setPricingReg(reg);
    setPricingForm({
      selected_weeks: Array.isArray(reg.selected_weeks) ? [...reg.selected_weeks] : [],
      payment_amount: reg.payment_amount != null ? String(reg.payment_amount) : '',
      deposit_amount: reg.deposit_amount != null ? String(reg.deposit_amount) : '',
    });
    setPricingOpen(true);
  };

  const toggleWeekSelection = (weekNum) => {
    setPricingForm((prev) => {
      const set = new Set(prev.selected_weeks);
      if (set.has(weekNum)) set.delete(weekNum);
      else set.add(weekNum);
      return { ...prev, selected_weeks: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const submitPricing = async () => {
    if (!pricingReg) return;
    const payload = {};
    if (pricingForm.selected_weeks.length > 0) {
      payload.selected_weeks = pricingForm.selected_weeks;
    }
    if (pricingForm.payment_amount !== '') {
      const v = Number(pricingForm.payment_amount);
      if (Number.isFinite(v) && v >= 0) payload.payment_amount = v;
    }
    if (pricingForm.deposit_amount !== '') {
      const v = Number(pricingForm.deposit_amount);
      if (Number.isFinite(v) && v >= 0) payload.deposit_amount = v;
    }
    if (Object.keys(payload).length === 0) {
      toast.error('Change at least one field');
      return;
    }
    try {
      setPricingSaving(true);
      // iter306k: send without refund_method first. Backend either
      // applies cleanly OR returns refund.required=true so we open the
      // refund confirmation modal.
      const { data } = await api.patch(`/camps/registrations/${pricingReg.id}/pricing`, payload);
      if (data?.refund?.required && !data.refund.method) {
        // Over-paid — collect refund method from admin then re-submit.
        setRefundConfirm({
          reg: pricingReg,
          overpaid: data.refund.overpaid,
          new_total: data.payment_amount,
          actual_paid: data.actual_paid,
          basePayload: payload,
        });
        setPricingOpen(false);
        return;
      }
      toast.success('Pricing updated');
      setPricingOpen(false);
      if (selectedCamp) fetchRegistrations(selectedCamp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setPricingSaving(false);
    }
  };

  // iter306k: confirm + submit refund alongside pricing update.
  const submitRefundConfirm = async () => {
    if (!refundConfirm) return;
    try {
      setRefundConfirming(true);
      const payload = {
        ...refundConfirm.basePayload,
        refund_method: refundMethod,
        refund_amount: refundConfirm.overpaid,
        refund_reason: refundReason || 'Pricing reduced',
      };
      const { data } = await api.patch(`/camps/registrations/${refundConfirm.reg.id}/pricing`, payload);
      toast.success(
        refundMethod === 'none'
          ? 'Pricing updated (refund waived)'
          : `Refund of ${cs()}${(data?.refund?.issued || refundConfirm.overpaid).toFixed(2)} issued via ${refundMethod}`,
      );
      setRefundConfirm(null);
      setRefundMethod('stripe');
      setRefundReason('');
      if (selectedCamp) fetchRegistrations(selectedCamp.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundConfirming(false);
    }
  };



  if (loading) {
    return (
      <DashboardLayout title="Camp Management">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Camp Management">
      <div className="space-y-6" data-testid="camp-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Tent className="w-6 h-6 text-green-600" />
              Camp Management
            </h1>
            <p className="text-slate-600">Create and manage special camp registrations</p>
          </div>
          <Button onClick={() => openCampDialog()} data-testid="create-camp-btn">
            <Plus className="h-4 w-4 mr-2" />
            Create Camp
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="camps">
              <Tent className="h-4 w-4 mr-2" />
              Camps ({camps.length})
            </TabsTrigger>
            <TabsTrigger value="registrations" disabled={!selectedCamp}>
              <Users className="h-4 w-4 mr-2" />
              Registrations {selectedCamp && `(${selectedCamp.name})`}
            </TabsTrigger>
          </TabsList>

          {/* Camps Tab */}
          <TabsContent value="camps" className="mt-6">
            {camps.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Tent className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <h3 className="text-lg font-medium text-slate-900">No Camps Yet</h3>
                  <p className="text-slate-500 mb-4">Create your first special camp</p>
                  <Button onClick={() => openCampDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Camp
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {camps.map(camp => (
                  <Card key={camp.id} className={!camp.is_active ? 'opacity-60' : ''}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{camp.name}</h3>
                            <Badge variant={camp.is_active ? 'default' : 'secondary'}>
                              {camp.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            <Badge variant="outline" className="capitalize">
                              {camp.camp_type}
                            </Badge>
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-slate-600 mb-2">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {camp.start_date} - {camp.end_date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {camp.start_time} - {camp.end_time}
                            </span>
                            {camp.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-4 w-4" />
                                {camp.location}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <DollarSign className="h-4 w-4" />
                              {cs()}{camp.fee}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {camp.registered_count || 0}{camp.max_participants ? `/${camp.max_participants}` : ''} registered
                            </span>
                          </div>
                          
                          <div className="flex flex-wrap gap-1 mt-2">
                            {camp.days_of_week?.map(day => (
                              <Badge key={day} variant="outline" className="text-xs capitalize">
                                {day.slice(0, 3)}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewRegistrations(camp)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View ({camp.registered_count || 0})
                          </Button>
                          <Switch
                            checked={camp.is_active}
                            onCheckedChange={() => toggleCamp(camp.id)}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => announceCamp(camp)}
                            disabled={!camp.is_active || announcingCampId === camp.id}
                            title={
                              camp.is_active
                                ? 'Notify all parents and customers that registration is open'
                                : 'Activate this camp before announcing'
                            }
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            data-testid={`announce-camp-${camp.id}`}
                          >
                            {announcingCampId === camp.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Megaphone className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openCampDialog(camp)}
                            data-testid={`edit-camp-${camp.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => duplicateCamp(camp)}
                            title="Duplicate this camp as a draft"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`duplicate-camp-${camp.id}`}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteCamp(camp.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations" className="mt-6">
            {selectedCamp && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>{selectedCamp.name} - Registrations</CardTitle>
                      <CardDescription>
                        {registrations.length} registration(s)
                      </CardDescription>
                    </div>
                    <Button variant="outline" onClick={() => setActiveTab('camps')}>
                      Back to Camps
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingRegistrations ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                  ) : registrations.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No registrations yet
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-2">Player</th>
                            <th className="text-left p-2">Parent</th>
                            <th className="text-left p-2">Contact</th>
                            <th className="text-left p-2">Weeks</th>
                            <th className="text-right p-2">Total Fees</th>
                            <th className="text-right p-2">Discount</th>
                            <th className="text-right p-2">Final Fee</th>
                            <th className="text-right p-2">Deposit</th>
                            <th className="text-right p-2">Balance</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-left p-2">Payment</th>
                            <th className="text-left p-2">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {registrations.map(reg => (
                            <tr key={reg.id} className="border-b">
                              <td className="p-2 font-medium">
                                <div>{reg.player_name}</div>
                                {reg.player_age && <div className="text-xs text-slate-500">Age: {reg.player_age}</div>}
                              </td>
                              <td className="p-2">{reg.parent_name}</td>
                              <td className="p-2">
                                <div className="text-xs">
                                  <div>{reg.parent_email}</div>
                                  {reg.parent_phone && <div>{reg.parent_phone}</div>}
                                </div>
                              </td>
                              <td className="p-2">
                                {reg.selected_weeks?.length > 0 ? (
                                  <div className="flex flex-wrap gap-1">
                                    {reg.selected_weeks.map(w => (
                                      <Badge key={w} variant="outline" className="text-xs">W{w}</Badge>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                              <td className="p-2 text-right" data-testid={`camp-reg-total-fees-${reg.id}`}>
                                <div className="font-medium tabular-nums">
                                  {cs()}{Number(reg.pricing_breakdown?.total_fees ?? reg.pricing_breakdown?.gross ?? 0).toFixed(2)}
                                </div>
                                {Number(reg.pricing_breakdown?.weeks || 0) > 0 && Number(reg.pricing_breakdown?.week_price || 0) > 0 && (
                                  <div className="text-xs text-slate-500">
                                    {reg.pricing_breakdown.weeks}w × {cs()}{Number(reg.pricing_breakdown.week_price).toFixed(2)}
                                  </div>
                                )}
                              </td>
                              <td className="p-2 text-right" data-testid={`camp-reg-discount-${reg.id}`}>
                                {Number(reg.pricing_breakdown?.discount_amount || 0) > 0 ? (
                                  <>
                                    <div className="font-medium tabular-nums text-rose-700">
                                      −{cs()}{Number(reg.pricing_breakdown.discount_amount).toFixed(2)}
                                    </div>
                                    {reg.pricing_breakdown.discount_reason && (
                                      <div className="text-xs text-slate-500 truncate max-w-[120px]" title={reg.pricing_breakdown.discount_reason}>
                                        {reg.pricing_breakdown.discount_reason}
                                      </div>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-2 text-right" data-testid={`camp-reg-final-fee-${reg.id}`}>
                                <div className="font-semibold tabular-nums">
                                  {cs()}{Number(reg.pricing_breakdown?.final_fee ?? reg.pricing_breakdown?.discounted_total ?? reg.payment_amount ?? 0).toFixed(2)}
                                </div>
                                {Number(reg.paid_amount || 0) > 0 && (
                                  <div className="text-xs text-green-700">
                                    Paid: {cs()}{Number(reg.paid_amount).toFixed(2)}
                                  </div>
                                )}
                                {reg.refund_status && (
                                  <div className="text-xs text-rose-700 mt-1">
                                    {reg.refund_status === 'fully_refunded' ? 'Fully refunded' : 'Partially refunded'}
                                    {Number(reg.refunded_amount || 0) > 0 && ` (${cs()}${Number(reg.refunded_amount).toFixed(2)})`}
                                  </div>
                                )}
                              </td>
                              <td className="p-2 text-right" data-testid={`camp-reg-deposit-${reg.id}`}>
                                {Number(reg.pricing_breakdown?.deposit_required || reg.deposit_amount || 0) > 0 ? (
                                  <>
                                    <div className="font-medium tabular-nums">
                                      {cs()}{Number(reg.pricing_breakdown?.deposit_required ?? reg.deposit_amount ?? 0).toFixed(2)}
                                    </div>
                                    <Badge
                                      variant={reg.deposit_paid ? 'default' : 'secondary'}
                                      className={`text-xs mt-1 ${reg.deposit_paid ? 'bg-green-600 hover:bg-green-700' : 'border-amber-500 text-amber-700'}`}
                                    >
                                      {reg.deposit_paid ? 'Paid' : 'Unpaid'}
                                    </Badge>
                                  </>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="p-2 text-right" data-testid={`camp-reg-balance-${reg.id}`}>
                                {Number(reg.due_amount || reg.pricing_breakdown?.balance_due || 0) > 0 ? (
                                  <div className="font-medium tabular-nums text-amber-700">
                                    {cs()}{Number(reg.due_amount ?? reg.pricing_breakdown?.balance_due ?? 0).toFixed(2)}
                                  </div>
                                ) : (
                                  <div className="font-medium tabular-nums text-green-700">$0.00</div>
                                )}
                              </td>
                              <td className="p-2">
                                <Badge variant={
                                  reg.status === 'confirmed' ? 'default' :
                                  reg.status === 'cancelled' ? 'destructive' : 'secondary'
                                }>
                                  {reg.status}
                                </Badge>
                              </td>
                              <td className="p-2">
                                <Badge variant={
                                  reg.payment_status === 'paid' ? 'default' :
                                  reg.payment_status === 'deposit_paid' ? 'outline' :
                                  reg.payment_status === 'refunded' ? 'destructive' : 'secondary'
                                } className={reg.payment_status === 'deposit_paid' ? 'border-amber-500 text-amber-700' : ''}>
                                  {reg.payment_status === 'deposit_paid' ? 'Deposit Paid' : reg.payment_status}
                                </Badge>
                              </td>
                              <td className="p-2">
                                <div className="flex items-center gap-1">
                                  {reg.status !== 'confirmed' && reg.status !== 'cancelled' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateRegistrationStatus(reg.id, 'confirmed')}
                                      title="Confirm"
                                    >
                                      <UserCheck className="h-4 w-4 text-green-600" />
                                    </Button>
                                  )}
                                  {reg.status !== 'cancelled' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updateRegistrationStatus(reg.id, 'cancelled')}
                                      title="Cancel"
                                    >
                                      <XCircle className="h-4 w-4 text-red-600" />
                                    </Button>
                                  )}
                                  {reg.payment_status !== 'paid' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => updatePaymentStatus(reg.id, 'paid')}
                                      title="Mark Paid"
                                    >
                                      <DollarSign className="h-4 w-4 text-green-600" />
                                    </Button>
                                  )}
                                  {reg.payment_status === 'paid' && reg.refund_status !== 'fully_refunded' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => { setRefundReg(reg); setRefundOpen(true); }}
                                      title="Issue Refund"
                                      className="text-amber-700"
                                      data-testid={`refund-camp-${reg.id}`}
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {/* iter306h: admin pricing controls */}
                                  {reg.status !== 'cancelled' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openDiscountDialog(reg)}
                                      title="Apply Discount"
                                      className="text-blue-700"
                                      data-testid={`apply-discount-${reg.id}`}
                                    >
                                      <Percent className="h-4 w-4" />
                                    </Button>
                                  )}
                                  {reg.status !== 'cancelled' && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => openPricingDialog(reg)}
                                      title="Update Pricing"
                                      className="text-purple-700"
                                      data-testid={`update-pricing-${reg.id}`}
                                    >
                                      <Edit2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Camp Create/Edit Dialog */}
        <Dialog open={campDialogOpen} onOpenChange={setCampDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCamp ? 'Edit Camp' : 'Create New Camp'}
              </DialogTitle>
              <DialogDescription>
                Set up a special camp registration form
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Basic Information</h4>
                
                <div>
                  <Label>Camp Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Summer Cricket Intensive"
                  />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <RichTextEditor
                    value={formData.description}
                    onChange={(value) => setFormData({ ...formData, description: value })}
                    placeholder="Describe the camp, what players will learn, etc."
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Camp Type *</Label>
                    <Select
                      value={formData.camp_type}
                      onValueChange={(value) => setFormData({ ...formData, camp_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="single">Single (One-time)</SelectItem>
                        <SelectItem value="recurring">Recurring (Multiple weeks)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Main Field"
                    />
                  </div>
                </div>
              </div>
              
              {/* Schedule */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Schedule</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date *</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Date *</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Days of Week *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {DAYS_OF_WEEK.map(day => (
                      <label
                        key={day.value}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                          formData.days_of_week.includes(day.value)
                            ? 'bg-blue-100 border-blue-500 text-blue-700'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.days_of_week.includes(day.value)}
                          onCheckedChange={() => handleDayToggle(day.value)}
                          className="hidden"
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time *</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Time *</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Registration Deadline</Label>
                  <Input
                    type="date"
                    value={formData.registration_deadline}
                    onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                  />
                </div>
              </div>
              
              {/* Pricing & Capacity */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Pricing & Capacity</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Single Fee ($) {formData.camp_type === 'single' ? '*' : ''}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.fee}
                      onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">Total fee for single-week camp</p>
                  </div>
                  <div>
                    <Label>Price Per Week ($) {formData.camp_type === 'recurring' && formData.weeks.length > 0 ? '*' : ''}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.price_per_week}
                      onChange={(e) => setFormData({ ...formData, price_per_week: e.target.value })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">For multi-week camps</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Non-Refundable Deposit ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.deposit_amount}
                      onChange={(e) => setFormData({ ...formData, deposit_amount: e.target.value })}
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-500 mt-1">Optional deposit to reserve spot</p>
                  </div>
                  <div>
                    <Label>Max Participants</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_participants}
                      onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                      placeholder="Leave blank for unlimited"
                    />
                  </div>
                </div>
                
                {/* Weeks Management for Multi-Week Camps */}
                {formData.camp_type === 'recurring' && (
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-base font-semibold">Camp Weeks</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGenerateWeeks}
                      >
                        <Plus className="h-4 w-4 mr-1" />
                        Generate Weeks
                      </Button>
                    </div>
                    
                    {formData.weeks.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">
                        Click "Generate Weeks" to create week slots based on start date
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {formData.weeks.map(week => (
                          <div 
                            key={week.week_number}
                            className={`flex items-center justify-between p-2 rounded border ${
                              week.is_available ? 'bg-white border-green-200' : 'bg-slate-100 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Badge variant={week.is_available ? 'default' : 'secondary'}>
                                Week {week.week_number}
                              </Badge>
                              <span className="text-sm">
                                {formatDateOnly(week.start_date, dateOpts)} - {formatDateOnly(week.end_date, dateOpts)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={week.is_available}
                                onCheckedChange={() => toggleWeekAvailability(week.week_number)}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeWeek(week.week_number)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Multi-Week Discount */}
                {formData.camp_type === 'recurring' && formData.weeks.length > 0 && (
                  <div className="border rounded-lg p-4 bg-green-50">
                    <Label className="text-base font-semibold text-green-800">Multi-Week Discount (Optional)</Label>
                    <p className="text-sm text-green-600 mb-3">Offer a discount for registering multiple weeks</p>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm">Discount Type</Label>
                        <Select
                          value={formData.multi_week_discount_type || 'none'}
                          onValueChange={(value) => setFormData({ ...formData, multi_week_discount_type: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger data-testid="multi-week-discount-type-select">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="percentage">Percentage Off</SelectItem>
                            <SelectItem value="fixed_per_week">$ Off Per Week</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm">Discount Value</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.multi_week_discount_value}
                          onChange={(e) => setFormData({ ...formData, multi_week_discount_value: e.target.value })}
                          placeholder={formData.multi_week_discount_type === 'percentage' ? '10' : '15'}
                          disabled={!formData.multi_week_discount_type}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.multi_week_discount_type === 'percentage' ? '% off total' : '$ off per week'}
                        </p>
                      </div>
                      <div>
                        <Label className="text-sm">Min Weeks for Discount</Label>
                        <Input
                          type="number"
                          min="2"
                          value={formData.min_weeks_for_discount}
                          onChange={(e) => setFormData({ ...formData, min_weeks_for_discount: e.target.value })}
                          placeholder="2"
                          disabled={!formData.multi_week_discount_type}
                        />
                        <p className="text-xs text-slate-500 mt-1">Minimum weeks to qualify</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Eligibility */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Eligibility</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Minimum Age</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.min_age}
                      onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
                      placeholder="Any"
                    />
                  </div>
                  <div>
                    <Label>Maximum Age</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.max_age}
                      onChange={(e) => setFormData({ ...formData, max_age: e.target.value })}
                      placeholder="Any"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Experience Levels</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {EXPERIENCE_LEVELS.map(level => (
                      <label
                        key={level.value}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                          formData.experience_levels.includes(level.value)
                            ? 'bg-green-100 border-green-500 text-green-700'
                            : 'bg-white border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Checkbox
                          checked={formData.experience_levels.includes(level.value)}
                          onCheckedChange={() => handleExperienceToggle(level.value)}
                          className="hidden"
                        />
                        {level.label}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Leave empty for all levels</p>
                </div>
              </div>
              
              {/* Form Options */}
              <div className="space-y-4">
                <h4 className="font-semibold text-slate-700">Registration Form Options</h4>
                
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.capture_experience}
                      onCheckedChange={(checked) => setFormData({ ...formData, capture_experience: checked })}
                    />
                    <span className="text-sm">Ask for Experience Level</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={formData.capture_medical_notes}
                      onCheckedChange={(checked) => setFormData({ ...formData, capture_medical_notes: checked })}
                    />
                    <span className="text-sm">Ask for Medical Notes</span>
                  </label>
                </div>
                
                <div>
                  <Label>Custom Field Label (Optional)</Label>
                  <Input
                    value={formData.additional_info_label}
                    onChange={(e) => setFormData({ ...formData, additional_info_label: e.target.value })}
                    placeholder="e.g., What are your goals for this camp?"
                  />
                </div>
                
                <div>
                  <Label>Terms & Conditions (Optional)</Label>
                  <RichTextEditor
                    value={formData.terms_and_conditions}
                    onChange={(value) => setFormData({ ...formData, terms_and_conditions: value })}
                    placeholder="Cancellation policy, liability waiver, etc."
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label>Active (Open for Registration)</Label>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setCampDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={saveCamp} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingCamp ? 'Update Camp' : 'Create Camp'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <RefundDialog
        open={refundOpen}
        onOpenChange={setRefundOpen}
        entityType="camp_registration"
        entity={refundReg}
        priceField="payment_amount"
        summaryLines={
          refundReg
            ? [
                { label: 'Participant', value: refundReg.player_name || refundReg.customer_name || '—' },
                { label: 'Camp', value: selectedCamp?.name || '—' },
              ]
            : []
        }
        allowCancelCheckbox={false}
        onRefunded={() => { setRefundOpen(false); setRefundReg(null); selectedCamp && fetchRegistrations(selectedCamp.id); }}
      />

      {/* iter306h: Apply Discount dialog */}
      <Dialog open={discountOpen} onOpenChange={setDiscountOpen}>
        <DialogContent className="max-w-md" data-testid="camp-discount-dialog">
          <DialogHeader>
            <DialogTitle>Apply Discount</DialogTitle>
            <DialogDescription>
              {discountReg && (
                <span>
                  {discountReg.player_name} — {selectedCamp?.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="disc-amt">Discount Amount ($)</Label>
              <Input
                id="disc-amt"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 105.00"
                value={discountForm.discount_amount}
                onChange={(e) => setDiscountForm({ ...discountForm, discount_amount: e.target.value })}
                data-testid="discount-amount-input"
              />
              <div className="text-xs text-slate-500 mt-1">
                Overrides any existing discount on this registration.
              </div>
            </div>
            <div>
              <Label htmlFor="disc-reason">Reason (optional)</Label>
              <Input
                id="disc-reason"
                placeholder="e.g. Sibling 10%, Friend of coach"
                value={discountForm.discount_reason}
                onChange={(e) => setDiscountForm({ ...discountForm, discount_reason: e.target.value })}
                data-testid="discount-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiscountOpen(false)}>Cancel</Button>
            <Button onClick={submitDiscount} disabled={discountSaving} data-testid="discount-submit-btn">
              {discountSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply Discount'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter306h: Update Pricing dialog */}
      <Dialog open={pricingOpen} onOpenChange={setPricingOpen}>
        <DialogContent className="max-w-lg" data-testid="camp-pricing-dialog">
          <DialogHeader>
            <DialogTitle>Update Pricing</DialogTitle>
            <DialogDescription>
              {pricingReg && (
                <span>
                  {pricingReg.player_name} — {selectedCamp?.name}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Week selection toggles */}
            <div>
              <Label>Selected Weeks</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {(selectedCamp?.weeks || []).map((w) => {
                  const num = w.week_number ?? w.number;
                  const isOn = pricingForm.selected_weeks.includes(num);
                  return (
                    <Button
                      key={num}
                      type="button"
                      variant={isOn ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => toggleWeekSelection(num)}
                      data-testid={`pricing-week-${num}`}
                    >
                      W{num}
                    </Button>
                  );
                })}
                {/* Fallback if camp doesn't expose weeks: show currently-selected */}
                {(!selectedCamp?.weeks || selectedCamp.weeks.length === 0) && pricingForm.selected_weeks.length > 0 && (
                  <span className="text-xs text-slate-500">
                    Currently: {pricingForm.selected_weeks.map((w) => `W${w}`).join(', ')}
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Click to toggle; total is auto-recomputed from new selection unless you override below.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pr-amt">Total ($)</Label>
                <Input
                  id="pr-amt"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="auto"
                  value={pricingForm.payment_amount}
                  onChange={(e) => setPricingForm({ ...pricingForm, payment_amount: e.target.value })}
                  data-testid="pricing-amount-input"
                />
                <div className="text-xs text-slate-500 mt-1">Leave blank = recalc from weeks.</div>
              </div>
              <div>
                <Label htmlFor="pr-dep">Deposit ($)</Label>
                <Input
                  id="pr-dep"
                  type="number"
                  min="0"
                  step="0.01"
                  value={pricingForm.deposit_amount}
                  onChange={(e) => setPricingForm({ ...pricingForm, deposit_amount: e.target.value })}
                  data-testid="pricing-deposit-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingOpen(false)}>Cancel</Button>
            <Button onClick={submitPricing} disabled={pricingSaving} data-testid="pricing-submit-btn">
              {pricingSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Update Pricing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter306k: Refund Confirmation modal — opened when /pricing
          returns refund.required because new total < amount already paid. */}
      <Dialog open={!!refundConfirm} onOpenChange={(open) => !open && setRefundConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="camp-refund-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirm Refund</DialogTitle>
            <DialogDescription>
              Pricing reduction creates an overpaid balance. How should we return it?
            </DialogDescription>
          </DialogHeader>
          {refundConfirm && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-slate-50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span>Already paid</span><strong>{cs()}{Number(refundConfirm.actual_paid).toFixed(2)}</strong></div>
                <div className="flex justify-between"><span>New total</span><strong>{cs()}{Number(refundConfirm.new_total).toFixed(2)}</strong></div>
                <div className="flex justify-between border-t pt-1 text-amber-700"><span>Refund due</span><strong>{cs()}{Number(refundConfirm.overpaid).toFixed(2)}</strong></div>
              </div>
              <div>
                <Label>Refund method</Label>
                <select
                  className="mt-1 w-full rounded border-slate-300 text-sm"
                  value={refundMethod}
                  onChange={(e) => setRefundMethod(e.target.value)}
                  data-testid="refund-method-select"
                >
                  <option value="stripe">Stripe (back to original card)</option>
                  <option value="cash">Cash</option>
                  <option value="check">Check</option>
                  <option value="zelle">Zelle</option>
                  <option value="venmo">Venmo</option>
                  <option value="account_credit">Account Credit (parent wallet)</option>
                  <option value="none">No refund (comp/adjustment)</option>
                </select>
              </div>
              <div>
                <Label htmlFor="ref-reason">Reason (optional)</Label>
                <Input
                  id="ref-reason"
                  placeholder="e.g. Week 3 conflict, requested by parent"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  data-testid="refund-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundConfirm(null)}>Cancel</Button>
            <Button onClick={submitRefundConfirm} disabled={refundConfirming} data-testid="refund-confirm-btn">
              {refundConfirming ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm Refund'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CampManagement;
